import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '@/lib/api-client';
import {
  boardSchema,
  catalogoSchema,
  detailSchema,
  metricasSchema,
  type Estagio,
  type Frente,
  type StatusSinal,
  type Temperatura,
} from './schemas';

const keys = {
  board: (frente?: Frente | '') => ['crm', 'board', frente ?? ''] as const,
  detail: (id: string) => ['crm', 'detail', id] as const,
  catalogo: () => ['crm', 'catalogo'] as const,
  metricas: () => ['crm', 'metricas'] as const,
};

const okSchema = z.object({ ok: z.boolean().optional() }).passthrough();
const createOportunidadeSchema = z.object({ id: z.coerce.number() });

export function useBoard(frente?: Frente | '') {
  return useQuery({
    queryKey: keys.board(frente),
    queryFn: ({ signal }) =>
      api(`/api/v2/crm${frente ? `?frente=${encodeURIComponent(frente)}` : ''}`, {
        schema: boardSchema,
        signal,
      }),
    staleTime: 30_000,
  });
}

export function useDetail(id: string | null) {
  return useQuery({
    queryKey: keys.detail(id ?? ''),
    queryFn: ({ signal }) =>
      api(`/api/v2/crm/${id}`, { schema: detailSchema, signal }),
    enabled: !!id,
    staleTime: 15_000,
  });
}

export function useCatalogo() {
  return useQuery({
    queryKey: keys.catalogo(),
    queryFn: ({ signal }) =>
      api('/api/v2/crm/catalogo', { schema: catalogoSchema, signal }),
    staleTime: 5 * 60_000,
  });
}

export interface CreateOportunidadeInput {
  cliente_cpf: string;
  imovel_id?: number | null;
  verificacao?: string | null;
  servico_sugerido: string;
  frente: Frente;
  detalhe?: string | null;
}

export function useCreateOportunidade() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOportunidadeInput) =>
      api('/api/v2/crm', {
        method: 'POST',
        body: input,
        schema: createOportunidadeSchema,
      }),
    onSuccess: () => {
      invalidateBoard(qc);
    },
  });
}

export function useMetricas() {
  return useQuery({
    queryKey: keys.metricas(),
    queryFn: ({ signal }) =>
      api('/api/v2/crm/metricas', { schema: metricasSchema, signal }),
    staleTime: 60_000,
  });
}

function invalidateBoard(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['crm', 'board'] });
  void qc.invalidateQueries({ queryKey: ['crm', 'metricas'] });
}

export function useTriagem(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { status_sinal: StatusSinal; motivo?: string }) =>
      api(`/api/v2/crm/${id}/triagem`, {
        method: 'PUT',
        body: input,
        schema: okSchema,
      }),
    onSuccess: () => {
      invalidateBoard(qc);
      void qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export type PerfilInput = Record<string, unknown>;

export interface QualificarInput {
  temperatura?: Temperatura;
  comercial_id?: number;
  potencial_min?: number;
  potencial_max?: number;
  prazo_abordagem?: string;
  motivo_qualificacao?: string;
  observacao?: string;
  perfil?: PerfilInput;
}

const userListItemSchema = z
  .object({
    id: z.coerce.number(),
    name: z.string().nullish(),
    email: z.string().nullish(),
    role: z.string().nullish(),
    cargo: z.string().nullish(),
  })
  .passthrough();

export function useUsers() {
  return useQuery({
    queryKey: ['users', 'list'] as const,
    queryFn: ({ signal }) =>
      api('/api/users', { schema: z.array(userListItemSchema), signal }),
    staleTime: 5 * 60_000,
  });
}

const perfilSavedSchema = z.object({
  ok: z.boolean().optional(),
  perfil: z.record(z.unknown()).nullish(),
});

export interface PropostaInput {
  canal?: string;
  valor?: number;
  validade?: string;
  pagamento?: string;
  escopo?: string;
  link?: string;
  probabilidade?: string;
  observacao?: string;
}

const propostaResponseSchema = z.object({
  ok: z.boolean().optional(),
  estagio: z.string().optional(),
});

export function useRegistrarProposta(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PropostaInput) =>
      api(`/api/v2/crm/${id}/proposta/registrar`, {
        method: 'POST',
        body: input,
        schema: propostaResponseSchema,
      }),
    onSuccess: () => {
      invalidateBoard(qc);
      void qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export interface AbordagemInput {
  canal?: string;
  quem_atendeu?: string;
  resultado: string;
  anotacoes?: string;
  proxima_acao?: string;
  proximo_contato_em?: string;
}

const abordagemResponseSchema = z.object({
  ok: z.boolean().optional(),
  estagio: z.string().optional(),
  registrado_em: z.string().optional(),
});

export function useRegistrarAbordagem(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AbordagemInput) =>
      api(`/api/v2/crm/${id}/abordagem/registrar`, {
        method: 'POST',
        body: input,
        schema: abordagemResponseSchema,
      }),
    onSuccess: () => {
      invalidateBoard(qc);
      void qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useSavePerfil(cpf: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (perfil: PerfilInput) =>
      api(`/api/v2/crm/cliente/${encodeURIComponent(cpf ?? '')}/perfil`, {
        method: 'PUT',
        body: perfil,
        schema: perfilSavedSchema,
      }),
    onSuccess: () => {
      // Invalida o detail de qualquer card desse cliente para refletir o perfil
      void qc.invalidateQueries({ queryKey: ['crm', 'detail'] });
    },
  });
}

export function useQualificar(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: QualificarInput) =>
      api(`/api/v2/crm/${id}/qualificar`, {
        method: 'PUT',
        body: input,
        schema: okSchema,
      }),
    onSuccess: () => {
      invalidateBoard(qc);
      void qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useAtributos() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      comercial_id,
      frente,
    }: {
      id: string;
      comercial_id?: number | null;
      frente?: Frente;
    }) =>
      api(`/api/v2/crm/${id}/atributos`, {
        method: 'PUT',
        body: {
          ...(comercial_id !== undefined ? { comercial_id } : {}),
          ...(frente !== undefined ? { frente } : {}),
        },
        schema: okSchema,
      }),
    onSuccess: (_d, vars) => {
      invalidateBoard(qc);
      void qc.invalidateQueries({ queryKey: keys.detail(vars.id) });
    },
  });
}

export function useMoverEstagio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, estagio }: { id: string; estagio: Estagio }) =>
      api(`/api/v2/crm/${id}/estagio`, {
        method: 'PUT',
        body: { estagio },
        schema: okSchema,
      }),
    onSuccess: (_d, vars) => {
      invalidateBoard(qc);
      void qc.invalidateQueries({ queryKey: keys.detail(vars.id) });
    },
    onError: (e) => {
      // Sem isto o 409 da state machine era engolido e o botão de mover
      // parecia morto (incidente 2026-07-20). Alert até o DS ganhar toast.
      window.alert(e instanceof Error ? e.message : 'Não foi possível mover o card.');
    },
  });
}

export function useFechar(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      resultado: 'ganho' | 'perdido';
      motivo?: string;
      motivo_categoria?: string;
      valor_fechado?: number;
    }) =>
      api(`/api/v2/crm/${id}/fechar`, {
        method: 'PUT',
        body: input,
        schema: okSchema,
      }),
    onSuccess: () => {
      invalidateBoard(qc);
      void qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useDossie(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { dossie_status?: string; dossie_url?: string }) =>
      api(`/api/v2/crm/${id}/dossie`, {
        method: 'PUT',
        body: input,
        schema: okSchema,
      }),
    onSuccess: () => {
      invalidateBoard(qc);
      void qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

const dossieTextoSchema = z.object({
  dossie_texto: z.string().nullish(),
  dossie_status: z.string().nullish(),
  dossie_modelo: z.string().nullish(),
  dossie_gerado_em: z.string().nullish(),
});

export function useDossieTexto(id: string | null) {
  return useQuery({
    queryKey: ['crm', 'dossie', id ?? ''] as const,
    queryFn: ({ signal }) =>
      api(`/api/v2/crm/${id}/dossie`, { schema: dossieTextoSchema, signal }),
    enabled: !!id,
    staleTime: 30_000,
  });
}

const dossieGerarSchema = z.object({
  ok: z.boolean().optional(),
  dossie_status: z.string().nullish(),
  dossie_texto: z.string().nullish(),
  modelo: z.string().nullish(),
  ms: z.coerce.number().nullish(),
});

export function useGerarDossie(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input?: { revisao?: string }) =>
      api(`/api/v2/crm/${id}/dossie/gerar`, {
        method: 'POST',
        body: input?.revisao ? { revisao: input.revisao } : {},
        schema: dossieGerarSchema,
      }),
    onSuccess: () => {
      invalidateBoard(qc);
      void qc.invalidateQueries({ queryKey: keys.detail(id) });
      void qc.invalidateQueries({ queryKey: ['crm', 'dossie', id] });
    },
  });
}

export function useComentar(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (texto: string) =>
      api(`/api/v2/crm/${id}/comentario`, {
        method: 'POST',
        body: { texto },
        schema: okSchema,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useEditarComentario(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventoId, texto }: { eventoId: number; texto: string }) =>
      api(`/api/v2/crm/${id}/comentario/${eventoId}`, {
        method: 'PUT',
        body: { texto },
        schema: okSchema,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useExcluirComentario(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventoId: number) =>
      api(`/api/v2/crm/${id}/comentario/${eventoId}`, {
        method: 'DELETE',
        schema: okSchema,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useUploadAnexoComentario(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventoId, file }: { eventoId: number; file: File }) => {
      const fd = new FormData();
      fd.append('file', file);
      return api(`/api/v2/crm/${id}/comentario/${eventoId}/upload`, {
        method: 'POST',
        body: fd,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useExcluirAnexoComentario(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventoId, filename }: { eventoId: number; filename: string }) =>
      api(`/api/v2/crm/${id}/comentario/${eventoId}/anexo/${encodeURIComponent(filename)}`, {
        method: 'DELETE',
        schema: okSchema,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}
